import React, { useEffect, useMemo } from "react";
import { Row, Col, Layout } from "antd";
import { useGetPlotQuery } from "../redux/api/plots";
import { useGetTableQuery } from "../redux/api/tables";
import { useGetBoxMetricQuery } from "../redux/api/boxMetrics";
import { useGetPageContentQuery } from "../redux/api/pageContent";
import { useSelector } from "react-redux";
import PlotContainer from "./PlotContainer";
import TableContainer from "./TableContainer";
import BoxMetricContainer from "./BoxMetricContainer";
import { Skeleton, theme } from "antd";
import { useDebounce } from "@uidotdev/usehooks";
// import ResponsiveCol from "../components/experimental/ResponsiveCol";

const ContentForPage = ({ pageName, extraApiArgs = {} }) => {
  const { token } = theme.useToken();
  const [dataJson, setDataJson] = React.useState({});

  const selectedStartDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['start_date']
  );
  const selectedEndDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['end_date']
  );
  const selectedCompareStartDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['compare_start_date']
  );
  const selectedCompareEndDate = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['compare_end_date']
  );
  const selectedDateGranularity = useSelector(
    (state) => state.genericReporting.selectedDropdownValues['frequency']
  );
  const selectedChannels = useSelector(
    (state) => state.genericReporting.selectedChannels
  );
  const selectedChannelGroups = useSelector(
    (state) => state.genericReporting.selectedGaChannels
  );
  const selectedDropdownValues = useSelector((state) => state.genericReporting.selectedDropdownValues)

  const ltrFilter = useSelector((state) => state.genericReporting.ltr_filter);
  const ltrVsLtv = useSelector((state) => state.genericReporting.ltr_vs_ltv);
  const selectedConfig = useSelector((state) => state.genericReporting.selectedConfig);
  const selectedCustomConfigCategories = useSelector((state) => state.campaignMappingsUi.selectedCustomConfigCategories);

  const {
    data: pageContent,
    isLoading,
    isError,
    error,
  } = useGetPageContentQuery({
    clientName: selectedDropdownValues['client_name'],
    pageName,
  });

  const { platform } = extraApiArgs;
  const requestData = useMemo(
    () => ({
      date_range: {
        start_date: selectedStartDate,
        end_date: selectedEndDate,
      },
      compare_date_range: {
        start_date: selectedCompareStartDate,
        end_date: selectedCompareEndDate,
      },
      frequency: selectedDateGranularity,
      selected_channel_groups: selectedChannelGroups,
      selected_config: selectedConfig,
      ltr_vs_ltv: ltrVsLtv,
      selected_custom_configs: selectedCustomConfigCategories,
      type: ltrFilter,
      ...selectedDropdownValues,
      ...extraApiArgs,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      selectedStartDate,
      selectedEndDate,
      selectedCompareStartDate,
      selectedCompareEndDate,
      selectedDateGranularity,
      selectedChannels,
      selectedChannelGroups,
      platform,
      ltrVsLtv,
      ltrFilter,
      selectedConfig,
      selectedDropdownValues,
      selectedCustomConfigCategories,
      // extraApiArgs,
    ]
  );

  // Debounce the requestData to prevent excessive API calls
  const debouncedRequestData = useDebounce(requestData, 500);

  useEffect(() => {
    setDataJson(debouncedRequestData);
  }, [debouncedRequestData]);

  useEffect(() => {
    if (isError) {
      console.error(error);
    }
    console.log("Fetched Plots:", pageContent);
  }, [pageContent, isError, error]);

  if (isLoading) return <Skeleton active />;
  if (isError) return <div>Error loading page content.</div>;
  if (!pageContent || pageContent.length === 0)
    return <div>No content available.</div>;

  const colsPerRow = 24; // Full row width in AntD grid system
  let currentRow = [];
  const rows = [];
  let currentSpan = 0;

  pageContent.content.forEach((content, index) => {
    const span = content.content_span; // Default span of 8
    if (currentSpan + span > colsPerRow) {
      rows.push([...currentRow]);
      currentRow = [];
      currentSpan = 0;
    }
    currentRow.push(
      <React.Fragment key={`content-${content.content_name}-${index}`}>
        {content.content_type === "sparkline" && (
          <Col key={content.content_name} span={span}>
            <PlotContainer
              useApi={useGetPlotQuery}
              useApiArgs={{
                data: {
                  plot_name: content.content_name,
                  ...dataJson,
                },
              }}
              isSparkline={true}
            />
          </Col>
        )}
        {content.content_type === "plot" && (
          <Col
            style={{
              height: "40.2vh",
            }}
            key={content.content_name}
            span={span}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
              }}
            >
              <PlotContainer
                useApi={useGetPlotQuery}
                useApiArgs={{
                  data: {
                    plot_name: content.content_name,
                    ...dataJson,
                  },
                }}
              />
            </div>
          </Col>
        )}
        {content.content_type === "long_plot" && (
          <Col
            style={{
              height: "90.2vh",
            }}
            key={content.content_name}
            span={span}
          >
            <div
              style={{
                height: "100%",
                width: "100%",
              }}
            >
              <PlotContainer
                useApi={useGetPlotQuery}
                useApiArgs={{
                  data: {
                    plot_name: content.content_name,
                    ...dataJson,
                  },
                }}
              />
            </div>
          </Col>
        )}
        {content.content_type === "table" && (
          <Col key={content.content_name} span={span}>
            <div
              className="inner-col-container-for-table-plot soft-shadow"
              style={{
                borderRadius: token.cardRadius,
              }}
            >
              <TableContainer
                useApi={useGetTableQuery}
                apiParams={{
                  data: {
                    table_name: content.content_name,
                    ...dataJson,
                  },
                }}
                height={"40.2vh"}
                includeTooltip={false}
                includeDownloadButton={false}
              />
            </div>
          </Col>
        )}
        {content.content_type === "box_metric" && (
          <Col key={content.content_name} span={span}>
            <BoxMetricContainer
              useApi={useGetBoxMetricQuery}
              apiParams={{
                data: {
                  metric_name: content.content_name,
                  ...dataJson,
                },
              }}
              boxMetricType={content.content_type}
              includeTooltip={false}
            />
          </Col>
        )}
        {content.content_type === "box_metric_long" && (
          <Col key={content.content_name} span={span}>
            <BoxMetricContainer
              useApi={useGetBoxMetricQuery}
              apiParams={{
                data: {
                  metric_name: content.content_name,
                  ...dataJson,
                },
              }}
              boxMetricType={content.content_type}
              includeTooltip={false}
            />
          </Col>
        )}
      </React.Fragment>
    );
    currentSpan += span;
  });
  if (currentRow.length > 0) rows.push([...currentRow]);

  return (
    <Layout.Content style={{ width: "100%", margin: 0, padding: 0 }}>
      {rows.map((row, rowIndex) => {
        const isBoxMetricsOnly = row.every((col) =>
          pageContent.content.some(
            (content) =>
              content.content_name === col.key &&
              (content.content_type === "box_metric" || content.content_type === "sparkline")
          )
        );

        return (
          <Row
            key={rowIndex}
            gutter={[16, 16]}
            style={{
              width: "100%",
              minHeight: isBoxMetricsOnly && "35vh",
              margin: 0,
              padding: 0,
              marginBottom: rowIndex === rows.length - 1 ? 0 : "16px",
            }}
          >
            {row}
          </Row>
        );
      })}
      {/* this is an experimental component that demonstrates responsive columns 
      think about creating user configs in settings that can be used to set the breakpoints for the responsive columns
      view component in "../components/experimental/ResponsiveCol";
        <ResponsiveCol />
      */}
    </Layout.Content>
  );
};

export default ContentForPage;
